import React, { createContext, useContext, useState, useCallback } from 'react';
import { GraphNode } from '../types/graph';
import { ExtractedEntity } from '../components/SignalUpload';

interface UploadedSignal {
  entities: ExtractedEntity[];
  uploadedAt: string;
}

interface UploadedSignalsContextType {
  uploadedNodes: GraphNode[];
  uploadedInterventions: any[];
  addSignal: (entities: ExtractedEntity[]) => void;
  hasUploads: boolean;
}

const UploadedSignalsContext = createContext<UploadedSignalsContextType>({
  uploadedNodes: [],
  uploadedInterventions: [],
  addSignal: () => {},
  hasUploads: false,
});

export const UploadedSignalsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [uploadedNodes, setUploadedNodes] = useState<GraphNode[]>([]);
  const [uploadedInterventions, setUploadedInterventions] = useState<any[]>([]);

  const addSignal = useCallback((entities: ExtractedEntity[]) => {
    // Add graph nodes
    const newNodes: GraphNode[] = entities.map((entity, index) => ({
      id: `uploaded-${Date.now()}-${index}`,
      name: entity.name,
      type: 'entity' as const,
      score: Math.round(entity.confidence * 100),
    }));
    setUploadedNodes(prev => [...prev, ...newNodes]);

    // Generate interventions for high-confidence entities
    const newInterventions = entities
      .filter(e => e.confidence >= 0.7)
      .map((entity, index) => ({
        intervention_id: `uploaded-int-${Date.now()}-${index}`,
        title: `Knowledge Transfer: ${entity.name}`,
        description: `${entity.description} — Schedule a knowledge transfer session to document and distribute this expertise.`,
        status: 'PENDING',
        urgency: entity.confidence >= 0.9 ? 'High' : 'Medium',
        entity_id: `uploaded-${Date.now()}-${index}`,
        entity_name: entity.name,
        type: 'knowledge_transfer',
        created_at: new Date().toISOString(),
      }));
    setUploadedInterventions(prev => [...prev, ...newInterventions]);
  }, []);

  return (
    <UploadedSignalsContext.Provider value={{
      uploadedNodes,
      uploadedInterventions,
      addSignal,
      hasUploads: uploadedNodes.length > 0,
    }}>
      {children}
    </UploadedSignalsContext.Provider>
  );
};

export const useUploadedSignals = () => useContext(UploadedSignalsContext);
